import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { filterObjects } from 'common';
import { useEffect, useState } from 'react';
import { CfSearchInput, CfSearchInputVariant } from '../../../../../cf-ui/SearchInput/CfSearchInput';
import { DeviceType, useBreakpoint } from '../../../../../hooks/useBreakpoint';
import { UserCheckboxItem } from '../UserCheckboxList/components/UserCheckboxLabel';
import UserCheckboxList from '../UserCheckboxList/UserCheckboxList';

export interface UserItem {
  userId: string;
  userDisplayName: string;
  userName: string;
}

export interface UserSearchItem extends UserItem {
  selected: boolean;
  disabled?: boolean;
}

export interface UserSearchProps {
  items: UserSearchItem[];
  onChange?: (users: UserSearchItem[]) => void;
  isLoading?: boolean;
  sx?: SxProps<Theme>;
  checkboxListSx?: SxProps<Theme>;
  greyBackground?: boolean;
  filterVariant?: CfSearchInputVariant;
  inputTestId?: string;
  searchButtonTestId?: string;
  selectAllCheckboxTestId?: string;
  checkboxTestId?: string;
  gap?: number | string | ((theme: Theme) => string | number);
}

const UserSearch = (props: UserSearchProps): JSX.Element => {
  const { deviceType } = useBreakpoint();
  const { items, isLoading, sx, checkboxListSx, filterVariant } = props;
  const [filterText, setFilterText] = useState<string>('');
  const [options, setOptions] = useState<UserCheckboxItem[]>([]);

  useEffect(() => {
    setOptions(props.items);
  }, [props.items]);

  const handleChange = (checkboxItems: UserCheckboxItem[]) => {
    const checkboxItemsObj = Object.fromEntries(checkboxItems.map((i) => [i.userId, i]));
    const updatedOptions: UserSearchItem[] = props.items.map((o: UserSearchItem) => {
      const newOption = checkboxItemsObj[o.userId];
      return { ...o, selected: newOption ? newOption.selected : o.selected };
    });

    setOptions(checkboxItems);
    props.onChange?.(updatedOptions);
  };

  const getFilteredOptions = (): UserCheckboxItem[] => {
    const filteredItems = filterObjects(items, filterText.toLowerCase().trim(), ['userDisplayName', 'userName']);

    const filteredIds = new Set(filteredItems.map((val: UserSearchItem) => val.userId));
    return options.filter((o: UserCheckboxItem) => filteredIds.has(o.userId.toLowerCase()));
  };

  const filteredItems = getFilteredOptions();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', ...sx }}>
      <Box sx={{ pb: 1 }}>
        <CfSearchInput
          placeholder='Search users'
          onSubmit={(text) => setFilterText(text)}
          onClear={() => setFilterText('')}
          autoFocus
          variant={filterVariant}
          testId={props.inputTestId}
          searchButtonTestId={props.searchButtonTestId}
          size={deviceType === DeviceType.Tablet ? 'medium' : 'small'}
          isResponsive
        />
      </Box>
      <UserCheckboxList
        items={filteredItems}
        onChange={handleChange}
        isLoading={isLoading}
        sx={{ maxHeight: '355px', ...checkboxListSx }}
        greyBackground={props.greyBackground}
        dataTestId={props.selectAllCheckboxTestId}
        dataAltTestId={props.checkboxTestId}
        gap={props.gap}
      />
    </Box>
  );
};

export const mapToUserSearchItems = (users: UserItem[], selectedUserIds: string[]): UserSearchItem[] => {
  const selectedIds = new Set(selectedUserIds);
  return users.map((user: UserItem) => ({ ...user, selected: selectedIds.has(user.userId) }));
};

export default UserSearch;
